import { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import valid from "card-validator";
import Cards from "react-credit-cards-2";
import "react-credit-cards-2/dist/es/styles-compiled.css";
import InputMask from "react-input-mask";
import { useFormContext } from "react-hook-form";

import { useFormStore } from "@/store/form";

export const creditCardExpressSchema = yup.object().shape({
  payment: yup.object().shape({
    mainName: yup.string().trim().required("Nome é obrigatório"),
    creditCard: yup
      .string()
      .test(
        "credit-card",
        "Cartão de crédito inválido",
        (value) => valid.number(value).isValid,
      )
      .required("Cartão de crédito é obrigatório"),
    dueMonth: yup
      .string()
      .required("Vencimento é obrigatório")
      .test(
        "due-Month",
        "Mês inválido",
        (value = "") => valid.expirationMonth(value).isValid,
      ),
    dueYear: yup
      .string()
      .required("Vencimento é obrigatório")
      .test(
        "due-Year",
        "Ano inválido",
        (value = "") => valid.expirationYear(value).isValid,
      ),
    securityCode: yup
      .string()
      .required("CVV é obrigatório")
      .test("cvv", "CVV inválido", (value) => valid.cvv(value).isValid),
  }),
});

type FocusedField = "name" | "number" | "expiry" | "cvc" | "";

export type CreditCardExpressProps = {
  setFormIsValid(isValid: boolean): void;
};

const inputBase =
  "w-full bg-transparent px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:bg-blue-50/30";

type TouchedFields = {
  mainName: boolean;
  creditCard: boolean;
  dueMonth: boolean;
  dueYear: boolean;
  securityCode: boolean;
};

const CreditCardExpress = ({ setFormIsValid }: CreditCardExpressProps) => {
  const payment = useFormStore((store) => store.value.payment);
  const { setValue, register } = useFormContext() || {};

  const [name, setName] = useState(payment?.mainName ?? "");
  const [creditCard, setCreditCard] = useState(payment?.creditCard ?? "");
  const [dueMonth, setDueMonth] = useState(payment?.dueMonth ?? "");
  const [dueYear, setDueYear] = useState(payment?.dueYear ?? "");
  const [securityCode, setSecurityCode] = useState(payment?.securityCode ?? "");
  const [focused, setFocused] = useState<FocusedField>("");

  // Cada campo só mostra erro depois que o usuário saiu dele (blur)
  const [touched, setTouched] = useState<TouchedFields>({
    mainName: false,
    creditCard: false,
    dueMonth: false,
    dueYear: false,
    securityCode: false,
  });

  const touch = useCallback((field: keyof TouchedFields) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Registra campos no react-hook-form
  useEffect(() => {
    register?.("payment.mainName");
    register?.("payment.creditCard");
    register?.("payment.dueMonth");
    register?.("payment.dueYear");
    register?.("payment.securityCode");
  }, [register]);

  const cardInfo = valid.number(creditCard);
  const isAmex = cardInfo.card?.type === "american-express";
  const cardMask = isAmex ? "9999 999999 99999" : "9999 9999 9999 9999";
  const cvvMask = isAmex ? "9999" : "999";

  const expiryForCard = useMemo(() => {
    const m = (dueMonth ?? "").trim();
    const y = String(dueYear ?? "").trim().slice(-2);
    return `${m}${y}`;
  }, [dueMonth, dueYear]);

  // Sync com react-hook-form — setValue sem validar (o form valida no submit)
  useEffect(() => {
    setValue?.("payment.mainName", name, { shouldDirty: true });
  }, [name, setValue]);
  useEffect(() => {
    setValue?.("payment.creditCard", creditCard, { shouldDirty: true });
  }, [creditCard, setValue]);
  useEffect(() => {
    setValue?.("payment.dueMonth", dueMonth, { shouldDirty: true });
  }, [dueMonth, setValue]);
  useEffect(() => {
    setValue?.("payment.dueYear", dueYear, { shouldDirty: true });
  }, [dueYear, setValue]);
  useEffect(() => {
    setValue?.("payment.securityCode", securityCode, { shouldDirty: true });
  }, [securityCode, setValue]);

  // Validação própria para habilitar/desabilitar botão
  useEffect(() => {
    const minCardLength = cardMask.length;
    const minCvvLength = cvvMask.length;
    const formIsValid = !!(
      name?.trim() &&
      creditCard?.length >= minCardLength &&
      dueMonth &&
      dueMonth !== "" &&
      dueYear &&
      dueYear !== " " &&
      dueYear !== "" &&
      securityCode?.length >= minCvvLength
    );
    setFormIsValid(formIsValid);
  }, [creditCard, dueMonth, dueYear, name, securityCode, setFormIsValid, cardMask, cvvMask]);

  // Validação inline por campo (só mostra se touched)
  const fieldErrors = useMemo(() => {
    const e: Record<string, string> = {};
    if (touched.mainName && !name?.trim()) e.mainName = "Nome é obrigatório";
    if (touched.creditCard && creditCard && !valid.number(creditCard).isValid)
      e.creditCard = "Cartão de crédito inválido";
    if (touched.creditCard && !creditCard) e.creditCard = "Cartão de crédito é obrigatório";
    if (touched.dueMonth && !dueMonth) e.dueMonth = "Mês é obrigatório";
    if (touched.dueYear && (!dueYear || dueYear === " ")) e.dueYear = "Ano é obrigatório";
    if (touched.securityCode && !securityCode) e.securityCode = "CVV é obrigatório";
    if (touched.securityCode && securityCode && !valid.cvv(securityCode).isValid)
      e.securityCode = "CVV inválido";
    return e;
  }, [name, creditCard, dueMonth, dueYear, securityCode, touched]);

  // Filtra meses passados quando o ano selecionado é o corrente
  const monthOptions = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const selectedYear = parseInt(String(dueYear), 10);

    const months = [];
    for (let m = 1; m <= 12; m++) {
      if (selectedYear === currentYear && m < currentMonth) continue;
      const val = String(m).padStart(2, "0");
      months.push(
        <option key={val} value={val}>
          {val}
        </option>,
      );
    }
    return months;
  }, [dueYear]);

  const yearOptions = useMemo(() => {
    const startYear = new Date().getFullYear();
    const options = [];
    for (let i = 0; i < 10; i++) {
      options.push(
        <option key={startYear + i} value={startYear + i}>
          {startYear + i}
        </option>,
      );
    }
    return options;
  }, []);

  // Se o mês selecionado ficou inválido ao mudar o ano, limpar
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const selectedYear = parseInt(String(dueYear), 10);
    const selectedMonth = parseInt(dueMonth, 10);

    if (selectedYear === currentYear && selectedMonth < currentMonth) {
      setDueMonth("");
    }
  }, [dueYear]);

  const hasError = (field: string) => !!fieldErrors[field];

  return (
    <div>
      {/* Cartão 3D animado */}
      <div className="mb-6 flex justify-center" data-openreplay-obscured>
        <Cards
          number={creditCard?.replace(/\D/g, "") ?? ""}
          expiry={expiryForCard}
          cvc={securityCode ?? ""}
          name={name ?? ""}
          focused={focused || undefined}
          locale={{ valid: "validade" }}
          placeholders={{ name: "SEU NOME AQUI" }}
        />
      </div>

      {/* Form Stripe-style */}
      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-[0_1px_4px_rgba(0,70,145,0.06),0_4px_16px_rgba(0,70,145,0.03)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-slate-50/80 px-4 py-3">
          <div className="flex items-center gap-2">
            <svg
              className="h-4 w-4 text-slate-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <rect x="1" y="4" width="22" height="16" rx="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
            <span className="text-[13px] font-semibold text-slate-600">
              Cartão de crédito
            </span>
          </div>
          <div className="flex gap-1">
            <span className="rounded bg-[#1a1f71] px-1.5 py-0.5 text-[9px] font-bold text-white">
              VISA
            </span>
            <span className="rounded bg-[#eb001b] px-1.5 py-0.5 text-[9px] font-bold text-white">
              MC
            </span>
            <span className="rounded bg-[#006fcf] px-1.5 py-0.5 text-[9px] font-bold text-white">
              AMEX
            </span>
            <span className="rounded bg-[#1a1a1a] px-1.5 py-0.5 text-[9px] font-bold text-white">
              ELO
            </span>
          </div>
        </div>

        {/* Nome do titular */}
        <div className={`border-b ${hasError("mainName") ? "border-red-300 bg-red-50/30" : "border-gray-100"}`}>
          <InputMask
            mask={new Array(255).fill(/\D/) as unknown as string}
            // @ts-expect-error - maskChar exists in react-input-mask but not in types
            maskChar=""
            className={inputBase}
            placeholder="Nome do titular no cartão"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={() => setFocused("name")}
            onBlur={() => { setFocused(""); touch("mainName"); }}
          />
          {hasError("mainName") && (
            <p className="px-4 pb-2 text-xs text-red-500">{fieldErrors.mainName}</p>
          )}
        </div>

        {/* Número do cartão */}
        <div className={`border-b ${hasError("creditCard") ? "border-red-300 bg-red-50/30" : "border-gray-100"}`}>
          <InputMask
            mask={cardMask}
            // @ts-expect-error - maskChar exists in react-input-mask but not in types
            maskChar=""
            type="tel"
            className={inputBase + " tracking-wider"}
            placeholder="Número do cartão"
            value={creditCard}
            onChange={(e) => setCreditCard(e.target.value)}
            onFocus={() => setFocused("number")}
            onBlur={() => { setFocused(""); touch("creditCard"); }}
          />
          {hasError("creditCard") && (
            <p className="px-4 pb-2 text-xs text-red-500">{fieldErrors.creditCard}</p>
          )}
        </div>

        {/* MM / AA / CVV */}
        <div className="grid grid-cols-3">
          <div className={`border-r ${hasError("dueMonth") ? "border-red-300 bg-red-50/30" : "border-gray-100"}`}>
            <select
              className={inputBase + " cursor-pointer appearance-none"}
              value={dueMonth}
              onChange={(e) => { setDueMonth(e.target.value); touch("dueMonth"); }}
              onFocus={() => setFocused("expiry")}
              onBlur={() => { setFocused(""); touch("dueMonth"); }}
            >
              <option value="">MM</option>
              {monthOptions}
            </select>
            {hasError("dueMonth") && (
              <p className="px-4 pb-2 text-xs text-red-500">{fieldErrors.dueMonth}</p>
            )}
          </div>
          <div className={`border-r ${hasError("dueYear") ? "border-red-300 bg-red-50/30" : "border-gray-100"}`}>
            <select
              className={inputBase + " cursor-pointer appearance-none"}
              value={dueYear}
              onChange={(e) => { setDueYear(e.target.value); touch("dueYear"); }}
              onFocus={() => setFocused("expiry")}
              onBlur={() => { setFocused(""); touch("dueYear"); }}
            >
              <option value="">AAAA</option>
              {yearOptions}
            </select>
            {hasError("dueYear") && (
              <p className="px-4 pb-2 text-xs text-red-500">{fieldErrors.dueYear}</p>
            )}
          </div>
          <div className={hasError("securityCode") ? "bg-red-50/30" : ""}>
            <InputMask
              mask={cvvMask}
              // @ts-expect-error - maskChar exists in react-input-mask but not in types
              maskChar=""
              type="tel"
              className={inputBase + " text-center"}
              placeholder={isAmex ? "CID" : "CVV"}
              value={securityCode}
              onChange={(e) => setSecurityCode(e.target.value)}
              onFocus={() => setFocused("cvc")}
              onBlur={() => { setFocused(""); touch("securityCode"); }}
            />
            {hasError("securityCode") && (
              <p className="px-4 pb-2 text-xs text-red-500">{fieldErrors.securityCode}</p>
            )}
          </div>
        </div>
      </div>

      {/* Nota de segurança */}
      <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
        <svg
          className="h-3.5 w-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
        Dados protegidos com criptografia
      </div>
    </div>
  );
};

export default CreditCardExpress;
